/**
 * @fileoverview useWatchlist — lista pessoal de leads "seguidos" pelo usuário.
 *
 * Persistida em localStorage como `bl-watchlist:<projectId>` (Set<string> serializado
 * como array). Inerentemente client-only — cada usuário tem a sua. Não é compartilhada
 * via DB para manter o feature simples e instantâneo (sem latência).
 *
 * Pattern espelhado do `bl-kanban-collapsed`: state setado uma vez no mount,
 * persistido write-through em cada toggle, recarregado quando o projeto muda.
 *
 * @example
 *   const { isWatched, toggle, count } = useWatchlist(activeProjectId);
 *   <button onClick={() => toggle(deal.id)}>
 *     {isWatched(deal.id) ? '★' : '☆'}
 *   </button>
 */
import { useCallback, useEffect, useState } from 'react';

export function useWatchlist(projectId: string | undefined | null) {
  const key = `bl-watchlist:${projectId || 'global'}`;

  const [ids, setIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(key);
      return new Set<string>(raw ? JSON.parse(raw) : []);
    } catch { return new Set<string>(); }
  });

  // Reload whenever the project changes — keeps lists scoped per CRM project.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(key);
      setIds(new Set<string>(raw ? JSON.parse(raw) : []));
    } catch { setIds(new Set<string>()); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const toggle = useCallback((id: string) => {
    setIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try { localStorage.setItem(key, JSON.stringify(Array.from(next))); } catch { /* empty */ }
      return next;
    });
  }, [key]);

  const isWatched = useCallback((id: string) => ids.has(id), [ids]);

  return { ids, isWatched, toggle, count: ids.size };
}
